import { Check, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function PackageCard({ pkg, accent = false }) {
  const [loading, setLoading] = useState(false);
  const recommended = pkg.recommended || accent;
  const isHosting = pkg.id?.startsWith("host_");

  const handleHostingCheckout = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/packages/${pkg.id}/deposit-link`);
      const link = data.payment_link;
      if (!link || link.includes("REPLACE_")) {
        toast.info("Hosting checkout link is being finalized — please check back shortly or contact us.");
        return;
      }
      window.location.href = link;
    } catch (e) {
      console.error(e);
      toast.error("Could not open hosting checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      data-testid={`package-card-${pkg.id}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative rounded-2xl p-8 transition-all duration-400 hover:-translate-y-1.5 ${
        recommended
          ? "bg-gradient-to-b from-blue-500/[0.12] to-white/[0.02] border border-blue-500/40 shadow-[0_0_50px_-12px_rgba(10,132,255,0.55)]"
          : "eq-card"
      }`}
    >
      {recommended && (
        <div className="absolute -top-3 left-8 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-blue-200 bg-blue-500/15 border border-blue-500/40 px-3 py-1 rounded-full backdrop-blur shadow-[0_0_20px_rgba(10,132,255,0.4)]">
          <Sparkles className="w-3 h-3" /> Recommended
        </div>
      )}
      {pkg.badge && (
        <div
          data-testid={`package-badge-${pkg.id}`}
          className="absolute -top-3 right-8 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-emerald-200 bg-emerald-500/15 border border-emerald-400/40 px-3 py-1 rounded-full backdrop-blur shadow-[0_0_20px_rgba(16,185,129,0.4)]"
        >
          <Sparkles className="w-3 h-3" /> {pkg.badge}
        </div>
      )}
      <div className="font-display text-2xl font-semibold tracking-tight">{pkg.name}</div>
      <div className="mt-1 text-xs uppercase tracking-widest text-gray-500">
        {pkg.custom ? "Tailored Quote" : pkg.startingAt ? "Starting at" : "Price"}
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className={`font-display text-4xl font-bold ${recommended ? "eq-gradient-text" : ""}`}>
          {pkg.price}
        </span>
        {pkg.suffix && <span className="text-gray-400">{pkg.suffix}</span>}
      </div>

      <ul className="mt-6 space-y-3">
        {pkg.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
            <span className="mt-0.5 grid place-items-center w-5 h-5 rounded-full bg-blue-500/15 border border-blue-500/30 shrink-0">
              <Check className="w-3 h-3 text-blue-300" strokeWidth={2.5} />
            </span>
            {f}
          </li>
        ))}
      </ul>

      <div className="mt-8 flex flex-col gap-3">
        {pkg.custom ? (
          <Link
            to={`/quote?package=${pkg.id}`}
            data-testid={`request-quote-${pkg.id}`}
            className="btn-primary w-full"
          >
            Request Custom Quote <ArrowRight className="w-4 h-4" />
          </Link>
        ) : isHosting ? (
          <>
            <button
              onClick={handleHostingCheckout}
              disabled={loading}
              data-testid={`hosting-checkout-${pkg.id}`}
              className="btn-primary w-full disabled:opacity-60"
            >
              {loading ? "Starting…" : "Start Hosting Plan"} <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to={`/quote?package=${pkg.id}`}
              data-testid={`hosting-quote-${pkg.id}`}
              className="btn-ghost w-full"
            >
              Request Custom Quote
            </Link>
            <p className="text-xs text-center text-gray-500 mt-1">
              Have a referral? Use code{" "}
              <span className="text-blue-300 font-medium">referral100</span> at checkout.
            </p>
          </>
        ) : (
          <>
            <Link
              to={`/intake/${pkg.id}`}
              data-testid={`continue-package-${pkg.id}`}
              className="btn-primary w-full"
            >
              Continue with Package <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={`/quote?package=${pkg.id}`}
              data-testid={`request-quote-${pkg.id}`}
              className="btn-ghost w-full"
            >
              Request Custom Quote
            </Link>
          </>
        )}
      </div>
    </motion.div>
  );
}
